import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {renameAllowed} from '@client/hub-actions-allowed'
import {Sync} from '@client/sync'
import {LabelBackgrounded} from '@components/common/base/label/label-backgrounded'
import {TextWithUrl} from '@components/common/base/text-with-url'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {TwTooltipText} from '@components/timeline-new/tasks/columns/containers'
import {
  useTimelineConfig,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {ColumnId} from '@components/timeline-new/types'
import {useHover, useTooltipVisible} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Column} from '@extensions/column'

interface EntityTitleProps {
  hubId: DataObjectId
  path: string
  title: string
  className?: string
  report?: boolean
  mirror?: boolean
  mirrorPath?: string
}

export const EntityTitle = observer((props: EntityTitleProps) => {
  const {hubId, path, title, className, report, mirrorPath} = props
  const [, store] = useTimelineNewStore()
  const config = useTimelineConfig()
  const titleRef = useRef<HTMLDivElement>(null)
  const tooltipVisible = useTooltipVisible(titleRef, [title])
  const {hover, onMouseOver, onMouseOut} = useHover()

  const softLimit = 150
  const hardLimit = 1024
  const realTitle = title || Lang.msg.unnamed
  const tooltipTitle = Util.limitStr(realTitle, hardLimit)

  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(hubId)
  if (!hub) {
    return null
  }
  const isMirrorColumn = Column.isMirror(hub)
  const isMirrorSubColumn = !isMirrorColumn && mirrorPath

  return (
    <Tooltip
      disabled={!tooltipVisible || !hover}
      content={<TwTooltipText>{tooltipTitle}</TwTooltipText>}
      offset={[0, 8]}
      placement="bottom"
    >
      <div className={cn('relative flex items-center gap-8 h-32')}>
        {report && (
          <LabelBackgrounded
            text={Lang.msg.dashboard.toUpperCase()}
            color={'text-sticker-gray'}
            paddings="px-6 py-2"
            bg={'bg-sticker-gray'}
          />
        )}

        {isMirrorColumn && !report && (
          <LabelBackgrounded
            text={Lang.msg.mirror.toUpperCase()}
            color={'text-sticker-gray'}
            paddings="px-6 py-2"
            bg={'bg-sticker-gray'}
          />
        )}

        {isMirrorSubColumn ? (
          <div className="micro-regular text-secondary">{mirrorPath}</div>
        ) : (
          <TextWithUrl
            ref={titleRef}
            className={className}
            text={title}
            bottom={-3}
            limit={softLimit}
            asLink
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onDoubleClick={e => {
              e.stopPropagation()
              if (config.static) {
                return
              }
              if (renameAllowed(hubId)) {
                store.startEditing(hubId, path, ColumnId.title)
              }
            }}
          />
        )}
      </div>
    </Tooltip>
  )
})
